import {
  DraftState,
  DraftStringInput,
  StringInfo,
  StringVariant,
} from 'src/features/string-group-detail/types';

/**
 * Finds this string's pending entry in the string group's draft changeset.
 *
 * `added` is searched as well as `edited` because a string created in the same
 * draft session lives there instead — the string detail page is reachable by URL
 * for either, and both mean "this string has unsubmitted changes".
 */
export function findDraftEntry(
  draft: DraftState | null,
  stringId: string,
): DraftStringInput | undefined {
  if (!draft) {
    return undefined;
  }
  return (
    draft.added.find((a) => a.stringId === stringId) ??
    draft.edited.find((e) => e.stringId === stringId)
  );
}

/** Two variant snapshots agree when both text and note match (absent ≡ empty). */
function variantMatches(
  pending: StringVariant,
  server: StringVariant,
): boolean {
  return (
    pending.sourceText === server.sourceText &&
    (pending.contextNote ?? '') === (server.contextNote ?? '')
  );
}

/**
 * Whether the view being rendered has an unsubmitted change behind it.
 *
 * The two views deliberately ask different questions:
 *
 * - **Base view** (`cxId` absent): any pending entry counts. An entry only
 *   exists because the user changed *something* on this string — its base pair,
 *   its format types, or one of its variants — and all of those are changes to
 *   the string this view represents.
 * - **Variant view**: only a change to *this* CX ID counts. A base-only edit
 *   copies the server's variants into the entry verbatim, so entry presence
 *   alone would wrongly flag every variant of an edited string. The pending
 *   variant is therefore compared against the server's own variant for the same
 *   CX ID, and a variant the server does not have yet is by definition pending.
 *
 * That comparison needs the server's variant list, which only the base
 * (no-`cxId`) response carries. `serverVariants` is therefore `undefined` — not
 * `[]` — when that response is unavailable, and the answer is then `false`: an
 * empty list is indistinguishable from "the server has no such variant", so
 * trusting it would flag every variant of a base-only edit. A genuinely empty
 * list (a string with no variants yet) still means pending, as it should.
 */
export function isDraftEntryModified(
  entry: DraftStringInput | undefined,
  cxId: string | undefined,
  serverVariants: StringVariant[] | undefined,
): boolean {
  if (!entry) {
    return false;
  }
  if (!cxId) {
    return true;
  }
  if (!serverVariants) {
    return false;
  }
  const pending = entry.variants.find((v) => v.cxId === cxId);
  if (!pending) {
    return false;
  }
  const server = serverVariants.find((v) => v.cxId === cxId);
  return !server || !variantMatches(pending, server);
}

export function isDraftAndStringIdentical(
  draft: DraftStringInput,
  stringInfo: StringInfo,
) {
  const variantsEqual =
    draft.variants.length === stringInfo.variants.length &&
    draft.variants.every((v, i) => variantMatches(v, stringInfo.variants[i]));
  return (
    draft.stringId === stringInfo.stringId &&
    draft.contextNote === stringInfo.contextNote &&
    draft.sourceLocale === stringInfo.sourceLocale &&
    draft.sourceText === stringInfo.sourceText &&
    draft.inlineMarkupType === stringInfo.inlineMarkupType &&
    draft.messageFormatType === stringInfo.messageFormatType &&
    variantsEqual
  );
}
